import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import '../../styles/users.scss';
import { apiGet } from '../../utils/api';

interface User {
  id: number;
  email: string;
  full_name: string | null;
  country: string | null;
  birthdate: string | null;
  is_active: boolean;
  is_admin: boolean;
  created_at: string;
}

interface PaginatedUsersResponse {
  users: User[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

const Users: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<PaginatedUsersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const data = await apiGet<PaginatedUsersResponse>(
          `/admin/users?page=${currentPage}&page_size=25`
        );
        setUsers(data);
      } catch (err) {
        console.error('Failed to fetch users:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [currentPage]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="users">
        <div className="users__container">
          <p>{t.common.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="users">
      <div className="users__container">
        <h1 className="users__title">{t.admin.users}</h1>

        {users && (
          <>
            <div className="users__list">
              <div className="users__table">
                <div className="users__header">
                  <div className="users__header-cell">{t.admin.id}</div>
                  <div className="users__header-cell">{t.admin.email}</div>
                  <div className="users__header-cell">{t.admin.fullName}</div>
                  <div className="users__header-cell">{t.admin.country}</div>
                  <div className="users__header-cell">{t.admin.admin}</div>
                  <div className="users__header-cell">{t.admin.active}</div>
                  <div className="users__header-cell">{t.admin.created}</div>
                </div>

                {users.users.map((user) => (
                  <div key={user.id} className="users__row">
                    <div className="users__cell">{user.id}</div>
                    <div className="users__cell">{user.email}</div>
                    <div className="users__cell">{user.full_name || '-'}</div>
                    <div className="users__cell">{user.country || '-'}</div>
                    <div className="users__cell">{user.is_admin ? t.common.yes : t.common.no}</div>
                    <div className="users__cell">{user.is_active ? t.common.yes : t.common.no}</div>
                    <div className="users__cell">{formatDate(user.created_at)}</div>
                  </div>
                ))}
              </div>
            </div>

            {users.total_pages > 1 && (
              <div className="users__pagination">
                <button
                  className="button_secondary users__pagination-button"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t.common.previous}
                </button>
                <span className="users__pagination-info">
                  {t.common.paginationShort
                    .replace('{page}', String(currentPage))
                    .replace('{totalPages}', String(users.total_pages))}{' '}
                  {t.common.totalCount.replace('{total}', String(users.total))}
                </span>
                <button
                  className="button_secondary users__pagination-button"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === users.total_pages}
                >
                  {t.common.next}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
